import React, { useRef, forwardRef, useState, useEffect } from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { DataTable } from "primereact/datatable";
import {
  Column,
  ColumnFilterElementTemplateOptions,
  ColumnFilterClearTemplateOptions,
  ColumnFilterApplyTemplateOptions,
} from "primereact/column";
import { ContextMenu } from "primereact/contextmenu";
import { MenuItem } from "primereact/menuitem";
import { Button } from "primereact/button";
import { IconEdit, IconDelete, IconEye } from "../../assets/icons/Icon";

export interface ColumnMeta<T = any> {
  field: keyof T | string;
  header: string | React.ReactNode;
  body?: (rowData: T) => React.ReactNode;
  dataType?: string;
  sortable?: boolean;
  filter?: boolean;
  filterField?: string;
  filterElement?: (
    options: ColumnFilterElementTemplateOptions
  ) => React.ReactNode;
  filterMatchMode?: string;
  filterFunction?: (value: any, filter: any, rowData?: T) => boolean;
  showFilterMatchModes?: boolean;
  customFilterWithButtons?: boolean;
  style?: React.CSSProperties;
}

export interface CustomAction<T> {
  icon: React.ReactNode;
  onClick: (item: T) => void;
  condition?: (item: T) => boolean;
}

interface CustomDatatableProps<T> {
  data: T[];
  columns: ColumnMeta<T>[];
  header?: React.ReactNode;
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  onView?: (item: T) => void;
  onRowDoubleClick?: (item: T) => void;
  selected?: T | null;
  onSelectionChange?: (selected: T | null) => void;
  sortable?: boolean;
  getRowId?: (item: T) => string | number;
  rowsPerPage?: number;
  contextMenuItems?: MenuItem[];
  extraContextMenuItems?: MenuItem[];
  hideViewMenuItem?: boolean;
  hideViewButton?: boolean;
  hideEditButton?: boolean;
  hideDeleteButton?: boolean;
  hideContextMenu?: boolean;
  mobileActions?: CustomAction<T>[];
}

function CustomDatatableInner<T extends Record<string, any>>(
  {
    data,
    columns,
    header,
    onEdit,
    onDelete,
    onView,
    onRowDoubleClick,
    selected,
    onSelectionChange,
    sortable,
    getRowId = (item) => (item as any).id,
    rowsPerPage = 12,
    contextMenuItems: customMenuItems,
    extraContextMenuItems = [],
    hideViewMenuItem,
    hideViewButton = true,
    hideEditButton,
    hideDeleteButton,
    hideContextMenu,
    mobileActions,
  }: CustomDatatableProps<T>,
  ref: React.ForwardedRef<DataTable<any>>
) {
  const isFullHD = useMediaQuery("(max-height: 1920px)");
  const isLargeScreen = useMediaQuery("(min-width: 2100px)");
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [effectiveRowsPerPage, setEffectiveRowsPerPage] = useState(rowsPerPage);
  const contextMenuRef = useRef<ContextMenu>(null);

  useEffect(() => {
    if (isFullHD) setEffectiveRowsPerPage(12);
    else if (isLargeScreen) setEffectiveRowsPerPage(25);
    else setEffectiveRowsPerPage(10);
  }, [isLargeScreen]);

  const getDefaultMenuItems = (): MenuItem[] => {
    const items: MenuItem[] = [];
    if (onView && !hideViewMenuItem) {
      items.push({
        label: "Просмотр",
        template: (item: MenuItem, options: any) => (
          <div onClick={options.onClick} className="p-menuitem-content">
            <IconEye size={14} />
            <span>{item.label}</span>
          </div>
        ),
        command: () => selected && onView(selected),
      });
    }
    if (onEdit && !hideEditButton) {
      items.push({
        label: "Редактировать",
        template: (item: MenuItem, options: any) => (
          <div onClick={options.onClick} className="p-menuitem-content">
            <IconEdit size={14} />
            <span>{item.label}</span>
          </div>
        ),
        command: () => selected && onEdit(selected),
      });
    }
    if (onDelete && !hideDeleteButton) {
      items.push({
        label: "Удалить",
        template: (item: MenuItem, options: any) => (
          <div onClick={options.onClick} className="p-menuitem-content">
            <IconDelete size={14} />
            <span>{item.label}</span>
          </div>
        ),
        command: () => selected && onDelete(selected),
      });
    }
    return items;
  };

  const wrapMenuItemWithTemplate = (item: MenuItem): MenuItem => {
    if (item.template) return item;
    return {
      ...item,
      template: (_item: MenuItem, options: any) => (
        <div onClick={options.onClick} className="p-menuitem-content">
          {item.icon && (
            <>
              {typeof item.icon === "string" ? (
                <i className={item.icon}></i>
              ) : (
                item.icon
              )}
            </>
          )}
          <span>{_item.label}</span>
        </div>
      ),
    };
  };

  const menuModel = (() => {
    if (hideContextMenu) return [];
    if (customMenuItems) return customMenuItems.map(wrapMenuItemWithTemplate);
    const defaultItems = getDefaultMenuItems();
    const extraItems = extraContextMenuItems.map(wrapMenuItemWithTemplate);
    return [...defaultItems, ...extraItems];
  })();

  const handleContextMenu = (e: any) => {
    if (!hideContextMenu && contextMenuRef.current && menuModel.length > 0) {
      contextMenuRef.current.show(e.originalEvent);
    }
  };

  const handleRowDoubleClick = (e: any) => {
    if (onRowDoubleClick) onRowDoubleClick(e.data);
  };

  const viewButtonTemplate = (rowData: T) => (
    <Button
      text
      onClick={(e) => {
        e.stopPropagation();
        onView?.(rowData);
      }}
    >
      <IconEye size={16} />
    </Button>
  );

  const editButtonTemplate = (rowData: T) => (
    <Button
      text
      onClick={(e) => {
        e.stopPropagation();
        onEdit?.(rowData);
      }}
    >
      <IconEdit size={16} />
    </Button>
  );

  const deleteButtonTemplate = (rowData: T) => (
    <Button
      text
      onClick={(e) => {
        e.stopPropagation();
        onDelete?.(rowData);
      }}
    >
      <IconDelete size={16} />
    </Button>
  );

  const filterClearTemplate = (options: ColumnFilterClearTemplateOptions) => {
    return (
      <Button
        type="button"
        label="Сбросить"
        onClick={options.filterClearCallback}
        outlined
      ></Button>
    );
  };

  const filterApplyTemplate = (options: ColumnFilterApplyTemplateOptions) => {
    return (
      <Button
        type="button"
        label="Применить"
        onClick={options.filterApplyCallback}
      ></Button>
    );
  };

  return (
    <>
      {!hideContextMenu && menuModel.length > 0 && (
        <ContextMenu model={menuModel} ref={contextMenuRef} />
      )}
      <DataTable
        ref={ref}
        value={data}
        header={header}
        paginator
        rows={effectiveRowsPerPage}
        // rowsPerPageOptions={[5, 10, 15, 20, 30, 50, 100]}
        scrollable
        scrollHeight="flex"
        emptyMessage="Нет данных"
        selectionMode="single"
        selection={selected}
        onSelectionChange={(e) => onSelectionChange?.(e.value as T | null)}
        onContextMenu={handleContextMenu}
        onContextMenuSelectionChange={(e) =>
          onSelectionChange?.(e.value as T | null)
        }
        onRowDoubleClick={handleRowDoubleClick}
        dataKey={getRowId as any}
        removableSort={sortable ?? false}
      >
        {/* {!hideViewButton && onView && (
          <Column
            body={viewButtonTemplate}
            style={{ width: "2.5rem" }}
            frozen
          />
        )}
        {!hideEditButton && onEdit && (
          <Column
            body={editButtonTemplate}
            style={{ width: "2.5rem" }}
            frozen
          />
        )} */}
        {columns?.map((col) => (
          <Column
            key={col.field as string}
            field={col.field as string}
            header={col.header}
            body={col.body}
            sortable={col.sortable ?? sortable ?? false}
            dataType={col.dataType}
            filter={col.filter}
            filterField={col.filterField}
            filterElement={col.filterElement}
            filterMatchMode={col.filterMatchMode}
            filterFunction={col.filterFunction as any}
            showFilterMatchModes={col.showFilterMatchModes || false}
            filterMatchModeOptions={[
              { label: "Содержит", value: "contains" },
              { label: "Начинается с", value: "startsWith" },
              { label: "Заканчивается на", value: "endsWith" },
              { label: "Равно", value: "equals" },
              { label: "Не равно", value: "notEquals" },
            ]}
            filterClear={
              col.customFilterWithButtons ? <></> : filterClearTemplate
            }
            filterApply={
              col.customFilterWithButtons ? <></> : filterApplyTemplate
            }
            filterMenuStyle={{ width: "16rem" }}
            style={col.style}
          />
        ))}
        {isMobile && mobileActions && mobileActions.length > 0 && (
          <Column
            body={(rowData: T) => (
              <div style={{ display: "flex", gap: "0.5rem" }}>
                {mobileActions
                  .filter(
                    (action) => !action.condition || action.condition(rowData)
                  )
                  .map((action, idx) => (
                    <Button
                      key={idx}
                      text
                      onClick={(e) => {
                        e.stopPropagation();
                        action.onClick(rowData);
                      }}
                    >
                      {action.icon}
                    </Button>
                  ))}
              </div>
            )}
            style={{ width: "auto" }}
            frozen
            alignFrozen="right"
          />
        )}
        {/* {!hideDeleteButton && onDelete && (
          <Column
            body={deleteButtonTemplate}
            style={{ width: "2.5rem" }}
            frozen
            alignFrozen="right"
          />
        )} */}
      </DataTable>
    </>
  );
}

const CustomDatatable = forwardRef(CustomDatatableInner) as <
  T extends Record<string, any>
>(
  props: CustomDatatableProps<T> & { ref?: React.ForwardedRef<DataTable<any>> }
) => React.ReactElement;

export default CustomDatatable;
